import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ApiLogEntry {
  id: string;
  timestamp: Date;

  // Request
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody: any;

  // Response
  status: number | null;
  statusText: string;
  responseHeaders: Record<string, string>;
  responseBody: any;

  // Meta
  duration: number | null;  // ms
  error: string | null;
  state: 'pending' | 'success' | 'error';
}

/**
 * v1.6.1227 — Secrets must never reach the log store.
 *
 * The interceptor hands us the request AFTER it has attached
 * `Authorization: Bearer <token>`, and the /login request body carries the
 * operator's PLAINTEXT PASSWORD. Both were being stored verbatim in a
 * 500-entry ring that is rendered at `dev/api-log` and can be written to a
 * file via `exportAsJson()`. Redaction happens HERE, at the store boundary,
 * so every caller is covered and no future interceptor change can leak by
 * forgetting to sanitize first.
 */
const REDACTED = '[REDACTED]';

/** Header names whose VALUE is a credential. Compared lower-case. */
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
]);

/** Body keys whose VALUE is a credential. Matched case-insensitively. */
const SENSITIVE_KEY_RE =
  /^(password|password_confirmation|current_password|new_password|passwd|secret|client_secret|api_key|apikey|openai_api_key|token|access_token|refresh_token|id_token|authorization|otp|one_time_password|pin|cvv|card_number)$/i;

/**
 * URLs whose payload is a bare credential regardless of key name.
 *
 * The settings endpoints return `{ data: { value: '<secret>' } }` — the key is
 * the generic `value`, so key-name matching alone cannot catch it and we must
 * not blanket-redact `value` (it would gut every other log). Match on the URL
 * instead and redact the whole body for those routes.
 */
const SENSITIVE_URL_RE =
  /(api[_-]?key|secret|token|password|credential|client[_-]?id)/i;

/** Depth cap — logged bodies are already JSON-cloned, but never recurse forever. */
const MAX_REDACT_DEPTH = 8;

function redactHeaders(headers: Record<string, string>): Record<string, string> {
  if (!headers) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADERS.has(k.toLowerCase()) ? REDACTED : v;
  }
  return out;
}

function redactBody(body: any, depth = 0): any {
  if (body == null) return body;
  if (depth >= MAX_REDACT_DEPTH) return body;
  if (Array.isArray(body)) return body.map(v => redactBody(v, depth + 1));
  if (typeof body !== 'object') return body;

  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(body)) {
    out[k] = SENSITIVE_KEY_RE.test(k) ? REDACTED : redactBody(v, depth + 1);
  }
  return out;
}

@Injectable({ providedIn: 'root' })
export class ApiLoggerService {
  private logs: ApiLogEntry[] = [];
  private logsSubject = new BehaviorSubject<ApiLogEntry[]>([]);
  
  logs$ = this.logsSubject.asObservable();
  
  private maxLogs = 500;
  private idCounter = 0;

  createEntry(method: string, url: string, headers: Record<string, string>, body: any): string {
    const id = `req-${++this.idCounter}-${Date.now()}`;
    
    const entry: ApiLogEntry = {
      id,
      timestamp: new Date(),
      method: method.toUpperCase(),
      url,
      requestHeaders: redactHeaders(headers),
      requestBody: SENSITIVE_URL_RE.test(url) ? REDACTED : redactBody(body),
      status: null,
      statusText: '',
      responseHeaders: {},
      responseBody: null,
      duration: null,
      error: null,
      state: 'pending',
    };

    this.logs.unshift(entry);

    // Cap log size
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    this.emit();
    return id;
  }

  completeEntry(
    id: string,
    status: number,
    statusText: string,
    responseHeaders: Record<string, string>,
    responseBody: any,
    duration: number
  ): void {
    const entry = this.logs.find(l => l.id === id);
    if (!entry) return;

    entry.status = status;
    entry.statusText = statusText;
    // The /login RESPONSE carries the freshly-issued token, so responses need
    // the same treatment as requests.
    entry.responseHeaders = redactHeaders(responseHeaders);
    entry.responseBody = SENSITIVE_URL_RE.test(entry.url)
      ? REDACTED
      : redactBody(responseBody);
    entry.duration = duration;
    entry.state = status >= 200 && status < 400 ? 'success' : 'error';

    this.emit();
  }

  errorEntry(id: string, error: string, status: number | null, duration: number): void {
    const entry = this.logs.find(l => l.id === id);
    if (!entry) return;

    entry.error = error;
    entry.status = status;
    entry.duration = duration;
    entry.state = 'error';

    this.emit();
  }

  clearLogs(): void {
    this.logs = [];
    this.emit();
  }

  getSnapshot(): ApiLogEntry[] {
    return [...this.logs];
  }

  getStats(): { total: number; success: number; errors: number; pending: number; avgDuration: number } {
    const total = this.logs.length;
    const success = this.logs.filter(l => l.state === 'success').length;
    const errors = this.logs.filter(l => l.state === 'error').length;
    const pending = this.logs.filter(l => l.state === 'pending').length;
    const completed = this.logs.filter(l => l.duration !== null);
    const avgDuration = completed.length > 0
      ? Math.round(completed.reduce((sum, l) => sum + (l.duration || 0), 0) / completed.length)
      : 0;

    return { total, success, errors, pending, avgDuration };
  }

  exportAsJson(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  private emit(): void {
    this.logsSubject.next([...this.logs]);
  }
}
